import * as cornerstone from '@cornerstonejs/core';

interface TrackedDicom {
  dicomUuid: string;
  imageIds: string[];
}

// Multi-viewport tracking: Map<viewportId, TrackedDicom>
const viewportTrackedDicoms = new Map<string, TrackedDicom>();

const preloadedDicoms = new Set<string>();

export const viewerCacheManager = {
  // Multi-viewport tracking - track DICOM for a specific viewport
  trackDicomForViewport(
    viewportId: string,
    dicomUuid: string,
    imageIds: string[]
  ): void {
    // Clear previous DICOM for this viewport if different
    const existing = viewportTrackedDicoms.get(viewportId);
    if (existing && existing.dicomUuid !== dicomUuid) {
      this.clearViewport(viewportId);
    }
    viewportTrackedDicoms.set(viewportId, { dicomUuid, imageIds });
  },

  // NEW: Clear cache for a specific viewport
  clearViewport(viewportId: string): void {
    const tracked = viewportTrackedDicoms.get(viewportId);
    if (!tracked) return;

    // Check if any other viewport is using the same DICOM
    let dicomInUseElsewhere = false;
    viewportTrackedDicoms.forEach((t, vpId) => {
      if (vpId !== viewportId && t.dicomUuid === tracked.dicomUuid) {
        dicomInUseElsewhere = true;
      }
    });

    // Only clear from cache if no other viewport is using it
    if (!dicomInUseElsewhere) {
      tracked.imageIds.forEach((imageId) => {
        try {
          cornerstone.cache.removeImageLoadObject(imageId);
        } catch {
          // Ignore errors - image might already be removed
        }
      });
      preloadedDicoms.delete(tracked.dicomUuid);
    }

    viewportTrackedDicoms.delete(viewportId);
  },

  // NEW: Clear all viewports (useful for cleanup)
  clearAllViewports(): void {
    const viewportIds = Array.from(viewportTrackedDicoms.keys());
    viewportIds.forEach((vpId) => this.clearViewport(vpId));
  },

  isPreloaded(dicomUuid: string): boolean {
    return preloadedDicoms.has(dicomUuid);
  },

  markPreloaded(dicomUuid: string): void {
    preloadedDicoms.add(dicomUuid);
  },

  clearPreloadStatus(dicomUuid: string): void {
    preloadedDicoms.delete(dicomUuid);
  },

  getCacheInfo(): { size: number; maxSize: number; sizeInMB: string } {
    const { cache } = cornerstone;
    const size = cache.getCacheSize();
    const maxSize = cache.getMaxCacheSize();
    return {
      size,
      maxSize,
      sizeInMB: `${(size / 1024 / 1024).toFixed(1)}MB / ${(maxSize / 1024 / 1024).toFixed(1)}MB`,
    };
  },
};
